import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaPlay } from 'react-icons/fa';
import GameImagePlaceholder from './GameImagePlaceholder';
import '../styles/WinningsTicker.css';
import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from '../utils/numberFormat';

const API_URL = getApiUrl();

const BOTTOM_TABS = [
    { key: 'top-mult', label: 'Крупнейшие коэффициенты' },
    { key: 'top-wins', label: 'Крупнейшие выигрыши' },
];

const WinningsTicker = ({ availableGames }) => {
    const [winnings, setWinnings] = useState([]);
    const [winningsData, setWinningsData] = useState(null);
    const [failedImages, setFailedImages] = useState(new Set());
    const [hoveredWinning, setHoveredWinning] = useState(null);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [activeTab, setActiveTab] = useState('now');
    const [bottomTab, setBottomTab] = useState(null);
    const [staticWinnings, setStaticWinnings] = useState({ 'top-mult': [], 'top-wins': [] });
    const winningIdRef = useRef(0);
    const tickerRef = useRef(null);
    const navigate = useNavigate();
    const { isAuth } = useAuth();
    const intervalRef = useRef(null);
    const topUpdateIntervalRef = useRef(null);
    const [isVisible, setIsVisible] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Отслеживание видимости страницы
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsVisible(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Отслеживание изменения размера окна
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            // Сбрасываем offset при изменении размера
            setCurrentOffset(0);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Загрузка данных из JSON файла
    useEffect(() => {
        const loadWinningsData = async () => {
            try {
                const response = await import('../data/winningsTicker.json');
                setWinningsData(response.default);
            } catch (error) {
                console.error('Ошибка загрузки данных выигрышей:', error);
            }
        };
        loadWinningsData();
    }, []);

    // Генерация фейкового ника с звёздочками
    const generateNickname = useCallback(() => {
        if (!winningsData) return 'Player***';
        const { nicknamePrefixes, nicknameSuffixes } = winningsData;
        const prefix = nicknamePrefixes[Math.floor(Math.random() * nicknamePrefixes.length)];
        const suffix = nicknameSuffixes[Math.floor(Math.random() * nicknameSuffixes.length)];
        const fullNick = prefix + suffix;
        if (fullNick.length <= 4) {
            return fullNick;
        }
        const start = fullNick.substring(0, 2);
        const end = fullNick.substring(fullNick.length - 2);
        const stars = '*'.repeat(Math.min(fullNick.length - 4, 6));
        return start + stars + end;
    }, [winningsData]);

    // Генерация реалистичного мультипликатора
    const generateMultiplier = useCallback((forTopSection = false) => {
        if (!winningsData) return '2';

        if (forTopSection) {
            // Для топа коэффициентов: только большие коэффициенты
            const random = Math.random();
            if (random < 0.6) {
                return Math.floor(Math.random() * 200) + 50; // 50-249 (60%)
            } else if (random < 0.85) {
                return Math.floor(Math.random() * 200) + 250; // 250-449 (25%)
            } else if (random < 0.98) {
                return Math.floor(Math.random() * 50) + 450; // 450-499 (13%)
            } else {
                return Math.floor(Math.random() * 50) + 500; // 500-549 (2%)
            }
        } else {
            // Для обычного раздела: реалистичное распределение
            const random = Math.random();
            if (random < 0.8) {
                // 80% - маленькие коэффициенты
                return Math.floor(Math.random() * 8.5) + 1.5; // 1.5-10x
            } else if (random < 0.95) {
                // 15% - средние коэффициенты  
                return Math.floor(Math.random() * 40) + 10; // 10-50x
            } else {
                // 5% - большие коэффициенты
                return Math.floor(Math.random() * 500) + 50; // 50-549x
            }
        }
    }, [winningsData]);

    // Генерация реалистичной суммы ставки и выигрыша
    const generateRealisticWin = useCallback((forTopSection = false) => {
        if (!winningsData) return { amount: 1000, currency: '₽', bet: 100 };
        const { currencies } = winningsData;

        // 90% выигрышей в рублях
        const useRubles = Math.random() < 0.9;
        let currency;
        if (useRubles) {
            currency = currencies.find(c => c.symbol === '₽') || currencies[0];
        } else {
            const otherCurrencies = currencies.filter(c => c.symbol !== '₽');
            currency = otherCurrencies[Math.floor(Math.random() * otherCurrencies.length)];
        }

        let betAmount, multiplier, winAmount;

        if (forTopSection) {
            // Для топа: только крупные выигрыши (максимум 600k)
            if (currency.symbol === '₽') {
                const random = Math.random();
                if (random < 0.6) {
                    winAmount = Math.random() * 150000 + 50000; // 50k-200k (60%)
                } else if (random < 0.85) {
                    winAmount = Math.random() * 200000 + 200000; // 200k-400k (25%)
                } else if (random < 0.98) {
                    winAmount = Math.random() * 150000 + 400000; // 400k-550k (13%)
                } else {
                    winAmount = Math.random() * 50000 + 550000; // 550k-600k (2%)
                }
            } else {
                // Для других валют пропорционально (максимум 600k в рублевом эквиваленте)
                const rubEquivalent = 50000;
                const maxRubEquivalent = 600000;
                const baseAmount = rubEquivalent / (currency.symbol === '$' ? 90 : currency.symbol === '€' ? 100 : 1);
                const maxAmount = maxRubEquivalent / (currency.symbol === '$' ? 90 : currency.symbol === '€' ? 100 : 1);
                const random = Math.random();
                if (random < 0.6) {
                    winAmount = Math.random() * (baseAmount * 3) + baseAmount;
                } else if (random < 0.85) {
                    winAmount = Math.random() * (baseAmount * 6) + (baseAmount * 3);
                } else if (random < 0.98) {
                    winAmount = Math.random() * (maxAmount * 0.25) + (maxAmount * 0.67);
                } else {
                    winAmount = Math.random() * (maxAmount * 0.08) + (maxAmount * 0.92);
                }
            }
        } else {
            // Для обычного раздела: реалистичная логика
            const playerType = Math.random();
            
            if (playerType < 0.7) {
                // 70% - мелкие ставки (100-1000₽)
                betAmount = Math.random() * 900 + 100; // 100-1000₽
                multiplier = generateMultiplier(false);
                winAmount = betAmount * multiplier;
                
                // Попадает в тикер только если выигрыш заметный (от 5000₽)
                if (winAmount < 5000) {
                    // Увеличиваем коэффициент до попадания в тикер (максимум 550x)
                    multiplier = Math.min(Math.max(multiplier, 5000 / betAmount), 550);
                    winAmount = betAmount * multiplier;
                }
            } else if (playerType < 0.95) {
                // 25% - средние ставки (1000-10000₽)
                betAmount = Math.random() * 9000 + 1000; // 1000-10000₽
                multiplier = generateMultiplier(false);
                winAmount = betAmount * multiplier;
                
                // Попадает в тикер если выигрыш от 15000₽
                if (winAmount < 15000) {
                    multiplier = Math.min(Math.max(multiplier, 15000 / betAmount), 550);
                    winAmount = betAmount * multiplier;
                }
            } else {
                // 5% - крупные ставки (10000₽+)
                betAmount = Math.random() * 50000 + 10000; // 10000-60000₽
                multiplier = generateMultiplier(false);
                winAmount = betAmount * multiplier;
                
                // Попадает в тикер если выигрыш от 50000₽
                if (winAmount < 50000) {
                    multiplier = Math.min(Math.max(multiplier, 50000 / betAmount), 550);
                    winAmount = betAmount * multiplier;
                }
            }
        }

        // Ограничиваем максимальный выигрыш 600k
        winAmount = Math.min(winAmount, 600000);

        // Форматирование суммы
        const decimals = currency.decimals || 0;
        const formatRandom = Math.random();
        if (decimals > 0) {
            if (formatRandom < 0.4) {
                winAmount = Math.round(winAmount);
            } else if (formatRandom < 0.7) {
                winAmount = winAmount.toFixed(2);
            } else {
                winAmount = winAmount.toFixed(1);
            }
        } else {
            if (formatRandom < 0.6) {
                winAmount = Math.round(winAmount);
            } else {
                winAmount = Math.floor(winAmount);
            }
        }

        return {
            amount: winAmount,
            currency: currency.symbol,
            bet: betAmount || Math.round(winAmount / multiplier)
        };
    }, [winningsData, generateMultiplier]);

    // Функция для получения URL изображения (как на главной странице)
    const getGameImage = useCallback((game) => {
        if (!game.image || game.image === '') return null;
        
        // Если путь начинается с / - это серверное изображение, добавляем API_URL
        if (game.image.startsWith('/')) {
            return `${API_URL}${game.image}`;
        }
        
        // Иначе это уже полный URL
        return game.image;
    }, []);

    // Генерация новых выигрышей
    const generateNewWinnings = useCallback((count = 5, forTopSection = false) => {
        if (!winningsData || !availableGames || availableGames.length === 0) return [];
        const newWinnings = [];
        for (let i = 0; i < count; i++) {
            const game = availableGames[Math.floor(Math.random() * availableGames.length)];
            const { amount, currency, bet } = generateRealisticWin(forTopSection);
            const multiplier = forTopSection ? generateMultiplier(true) : Math.round((amount / bet) * 10) / 10;
            const gameImage = getGameImage(game) || '/assets/game-placeholder.png';
            newWinnings.push({
                id: winningIdRef.current++,
                gameUuid: game.uuid,
                gameImage: gameImage,
                gameName: game.name,
                provider: game.provider,
                amount: amount,
                currency: currency,
                multiplier: multiplier,
                bet: bet,
                nickname: generateNickname(),
                timestamp: Date.now()
            });
        }
        return newWinnings;
    }, [winningsData, availableGames, generateNickname, generateMultiplier, generateRealisticWin, getGameImage]);

    // Функция обновления топов
    const updateTops = useCallback(() => {
        if (!winningsData || !availableGames || availableGames.length === 0) return;

        const topMultData = generateNewWinnings(30, true);
        const topMult = [...topMultData].sort((a, b) => parseFloat(b.multiplier) - parseFloat(a.multiplier)).slice(0, 20);

        const topWinsData = generateNewWinnings(30, true);
        const topWins = [...topWinsData].sort((a, b) => {
            const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
            const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
            return amountB - amountA;
        }).slice(0, 20);

        setStaticWinnings({ 'top-mult': topMult, 'top-wins': topWins });
    }, [winningsData, availableGames, generateNewWinnings]);

    // Первичная загрузка данных и генерация топов
    useEffect(() => {
        if (winningsData && availableGames && availableGames.length > 0) {
            const initialWinnings = generateNewWinnings(20, false); // обычные выигрыши
            setWinnings(initialWinnings);

            // Генерируем топы
            updateTops();
            setCurrentOffset(0);
        }
    }, [winningsData, availableGames, generateNewWinnings, updateTops]);

    // Функция добавления нового элемента и движения слайдера
    const addNewWinningAndScroll = useCallback(() => {
        // Определяем размеры в зависимости от размера экрана
        const screenWidth = window.innerWidth;
        let itemWidth, gap;

        if (screenWidth <= 480) {
            itemWidth = 200;
            gap = 15;
        } else if (screenWidth <= 768) {
            itemWidth = 220;
            gap = 20;
        } else {
            itemWidth = 270;
            gap = 30;
        }

        const itemWithGap = itemWidth + gap;

        if (activeTab === 'now') {
            // Для обычного раздела: добавляем новый элемент и двигаем слайдер
            const newWinning = generateNewWinnings(1, false)[0];
            if (newWinning) {
                setWinnings(prev => [...prev, newWinning]);
                setCurrentOffset(prev => prev + itemWithGap);
            }
        } else {
            // Для топов: просто двигаем слайдер по существующим элементам
            const currentWinnings = staticWinnings[activeTab];
            if (currentWinnings && currentWinnings.length > 0 && tickerRef.current) {
                const containerWidth = tickerRef.current.offsetWidth;
                const visibleItems = Math.floor(containerWidth / itemWithGap);
                const totalItems = currentWinnings.length;

                // Если элементов меньше чем помещается в контейнер, не двигаем
                if (totalItems <= visibleItems) {
                    return;
                }

                setCurrentOffset(prev => {
                    const currentItemIndex = Math.round(prev / itemWithGap);
                    const nextItemIndex = currentItemIndex + 1;

                    // Если достигли конца, сбрасываем в начало
                    if (nextItemIndex >= totalItems - visibleItems + 1) {
                        return 0;
                    }

                    return nextItemIndex * itemWithGap;
                });
            }
        }
    }, [activeTab, generateNewWinnings, staticWinnings, windowWidth]);

    // Автоматическое движение слайдера каждые 5 секунд для всех вкладок
    useEffect(() => {
        if (!winningsData || !availableGames || availableGames.length === 0 || !isVisible) {
            return;
        }

        // Очищаем предыдущий интервал
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Устанавливаем новый интервал
        intervalRef.current = setInterval(addNewWinningAndScroll, 5000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [addNewWinningAndScroll, activeTab, winningsData, availableGames, isVisible]);

    // Автоматическое обновление топов каждые 30 секунд
    useEffect(() => {
        if (!winningsData || !availableGames || availableGames.length === 0 || !isVisible) {
            return;
        }

        // Очищаем предыдущий интервал
        if (topUpdateIntervalRef.current) {
            clearInterval(topUpdateIntervalRef.current);
        }

        // Устанавливаем новый интервал
        topUpdateIntervalRef.current = setInterval(updateTops, 30000);

        return () => {
            if (topUpdateIntervalRef.current) {
                clearInterval(topUpdateIntervalRef.current);
            }
        };
    }, [updateTops, winningsData, availableGames, isVisible]);

    // Перезапуск интервалов при возвращении видимости страницы
    useEffect(() => {
        if (isVisible && winningsData && availableGames && availableGames.length > 0) {
            // Небольшая задержка для корректного перезапуска после возвращения видимости
            const timeout = setTimeout(() => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = setInterval(addNewWinningAndScroll, 5000);
                }
                if (topUpdateIntervalRef.current) {
                    clearInterval(topUpdateIntervalRef.current);
                    topUpdateIntervalRef.current = setInterval(updateTops, 30000);
                }
            }, 100);

            return () => clearTimeout(timeout);
        }
    }, [isVisible, addNewWinningAndScroll, updateTops, winningsData, availableGames]);

    // Сброс offset при смене вкладки
    useEffect(() => {
        setCurrentOffset(0);
    }, [activeTab]);

    // Обработка клика на выигрыш
    const handleWinningClick = (winning) => {
        if (winning.gameUuid) {
            const gameUrl = `/gis-game/${winning.gameUuid}${!isAuth ? '?demo=true' : ''}${isAuth ? '?' : '&'}name=${encodeURIComponent(winning.gameName)}`;
            navigate(gameUrl);
        }
    };

    // Выбор данных для отображения
    let displayWinnings = winnings;
    if (activeTab === 'top-mult') displayWinnings = staticWinnings['top-mult'];
    if (activeTab === 'top-wins') displayWinnings = staticWinnings['top-wins'];

    if (!winningsData || !availableGames || availableGames.length === 0) {
        return null;
    }

    // Обработчик клика на нижние вкладки
    const handleBottomTabClick = (tabKey) => {
        if (bottomTab === tabKey) {
            // Если кликнули на уже активную вкладку - возвращаемся к "Сейчас выигрывают"
            setBottomTab(null);
            setActiveTab('now');
        } else {
            // Переключаемся на выбранную вкладку
            setBottomTab(tabKey);
            setActiveTab(tabKey);
        }
    };

    return (
        <div className="winnings-ticker-wrapper">
            <div className="winnings-ticker-inner">
                {/* Заголовок сверху */}
                <div className="winnings-ticker-header">
                    Сейчас выигрывают
                </div>
                
                <div className="winnings-ticker-container">
                    <div className="winnings-ticker" ref={tickerRef}>
                        <div
                            className="winnings-ticker-content"
                            style={{
                                transform: `translateX(-${currentOffset}px)`,
                                transition: 'transform 0.8s ease-in-out'
                            }}
                        >
                            {displayWinnings.map((win) => (
                                <div
                                    key={win.id}
                                    className="winning-item"
                                    onMouseEnter={() => setHoveredWinning(win.id)}
                                    onMouseLeave={() => setHoveredWinning(null)}
                                    onClick={() => handleWinningClick(win)}
                                >
                                    <div className="winning-game-image">
                                        {failedImages.has(win.id) ? (
                                            <GameImagePlaceholder
                                                gameName={win.gameName}
                                                provider={win.provider}
                                                width={70}
                                                height={40}
                                            />
                                        ) : (
                                            <img
                                                src={win.gameImage}
                                                alt={win.gameName}
                                                style={{ objectFit: 'contain' }}
                                                onError={() => {
                                                    setFailedImages(prev => new Set([...prev, win.id]));
                                                }}
                                            />
                                        )}
                                        {hoveredWinning === win.id && (
                                            <div className="winning-play-overlay">
                                                <FaPlay className="play-icon" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="winning-info">
                                        <div className="winning-player">{win.nickname}</div>
                                        <div className="winning-details">
                                            <span className="winning-amount">
                                                {win.currency}{formatBalanceLocale(win.amount)}
                                            </span>
                                            <span className="winning-multiplier">{win.multiplier}x</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Кнопки снизу */}
                <div className="winnings-ticker-bottom-tabs">
                    {BOTTOM_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`winnings-ticker-bottom-tab ${bottomTab === tab.key ? 'active' : ''}`}
                            onClick={() => handleBottomTabClick(tab.key)}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WinningsTicker; 